// @ts-nocheck

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { theme } from 'app/theme'
import React, { memo } from 'react'
import { Clock, Star } from 'react-feather'
import Fade from 'react-reveal/Fade'

export interface Props {
  list?: string[]
  title?: string
  variant?: Variant
}

export const Info = memo(function Info({
  list,
  title,
  variant = 'default',
}: Props) {
  if (!list) {
    return null
  }

  return (
    <Container variant={variant}>
      <Wrapper>
        <Text variant={variant}>
          {title ? (
            <Fade bottom distance="3.75rem">
              <Title>{title}</Title>
            </Fade>
          ) : null}
          <List>
            {list.map((item, index) => (
              <Fade key={index} bottom distance="3.75rem">
                <Item className="info-item">
                  {item.icon ? (
                    item.icon === 'clock' ? (
                      <Clock />
                    ) : item.icon === 'star' ? (
                      <Star />
                    ) : item.icon === 'leaf' ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24.173"
                        height="24"
                        viewBox="0 0 24.173 24"
                      >
                        <path
                          d="M39.61,16.632,39.54,16H38.8c-1.123,0-11.263.211-17.4,7.509-3.744,4.486-2.623,11-2.021,13.448-1.888,1.05-3.362,1.655-3.382,1.675a.712.712,0,0,0-.386.912.72.72,0,0,0,.667.456.472.472,0,0,0,.281-.07,30.264,30.264,0,0,0,3.383-1.621l.126.042a18.712,18.712,0,0,0,5.93.982,12.1,12.1,0,0,0,9.544-4.07C40.768,29.123,39.645,17.123,39.61,16.632ZM34.452,34.316A10.551,10.551,0,0,1,26,37.895a18.12,18.12,0,0,1-4.387-.563,26.365,26.365,0,0,0,3.229-2.349c3.3-2.842,6.456-8.035,6.6-8.246A.716.716,0,0,0,30.207,26c-.035.035-3.193,5.193-6.316,7.895a25.717,25.717,0,0,1-3.247,2.324c-.557-2.463-1.315-8.062,1.844-11.8,5.088-6.105,13.263-6.912,15.754-7.018C38.382,19.965,38.628,29.4,34.452,34.316Z"
                          transform="translate(-15.562 -16)"
                          fill={theme.colors.variants.primaryLight1}
                          stroke="none"
                        />
                      </svg>
                    ) : null
                  ) : null}
                  <ItemLabel
                    dangerouslySetInnerHTML={{
                      __html: item.text,
                    }}
                  />
                </Item>
              </Fade>
            ))}
          </List>
        </Text>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section<ContainerProps>`
  background: ${({ theme }) => theme.colors.variants.neutralDark1};
  padding: 9rem 10vw;

  @media (max-width: 1199px) {
    padding: 0;
  }

  ${({ theme, variant }) => {
    switch (variant) {
      case 'light':
        return css`
          background: ${theme.colors.variants.primaryLight1};
          padding-top: 0;
          padding-bottom: 7.5rem;
          > div {
            max-width: 880px;
          }
          .info-item {
            margin-top: 2.25rem;
            &:before {
              display: none;
            }
          }

          @media (max-width: 1199px) {
            margin-top: -7.5rem;
            padding: 0 0 3.75rem;
          }
        `
      case 'dark':
        return css`
          padding-top: 0;
          padding-bottom: 7.5rem;
          > div {
            max-width: 880px;
          }
          .info-item {
            margin-top: 2.25rem;
            &:before {
              display: none;
            }
            svg {
              stroke: ${theme.colors.variants.primaryLight1};
            }
          }

          @media (max-width: 1199px) {
            margin-top: -5.625rem;
            padding: 0 0 3.75rem;
          }
        `
    }
  }}
`

const Wrapper = styled.div`
  max-width: 684px;
  margin: auto;
  padding: 3.75rem;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: url('/wave.svg') no-repeat center;
    background-size: cover;
    opacity: 0.16;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 1199px) {
    max-width: none;
    padding: 0;
    &:before {
      display: none;
    }
  }
`

const Text = styled.div<ContainerProps>`
  background: ${({ theme }) => theme.colors.variants.neutralDark1};
  padding: 3.75rem;
  position: relative;
  text-align: center;
  z-index: 2;

  @media (max-width: 1199px) {
    padding: 40px 8.454vw;
  }

  ${({ theme, variant }) => {
    switch (variant) {
      case 'light':
        return css`
          background: ${theme.colors.variants.primaryLight1};
        `
    }
  }}
`

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.875rem;
  font-weight: 400;
  letter-spacing: 0.0563rem;
  line-height: 2.25rem;
  text-transform: uppercase;
`

const List = styled.div`
  margin-top: 1.875rem;
  .react-reveal {
    &:first-of-type {
      > div {
        &:before {
          display: none;
        }
      }
    }
  }
`

const Item = styled.div`
  &:before {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    background: ${({ theme }) => theme.colors.variants.primaryLight1};
    border-radius: 50%;
    margin: 12px auto;
  }
  svg {
    width: auto;
    height: 1.5rem;
    margin-bottom: 0.75rem;
    stroke: ${({ theme }) => theme.colors.variants.neutralDark1};
    stroke-width: 1.5;
  }
`

const ItemLabel = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  line-height: 1.875rem;
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'light' | 'dark'
